import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock,
  Send,
  CheckCircle
} from 'lucide-react';
import { contactAPI } from '../services/api';
import CommonContactUs from './CommonContactUs';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // await contactAPI.sendMessage(formData);
      
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      // Optionally show a toast or error message here
    }
    setIsSubmitting(false);
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      details: ['contact@techmind.com', 'support@techmind.com'],
      action: 'mailto:contact@techmind.com'
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: ['+1 (555) 123-4567'],
      action: 'tel:+15551234567'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      details: ['123 Tech Avenue, Suite 100', 'Silicon Valley, CA 94025'],
      action: 'https://maps.google.com'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: ['Mon - Fri: 9:00 AM - 6:00 PM', 'Sat: 10:00 AM - 2:00 PM'],
      action: null
    }
  ];

  const offices = [
    {
      city: 'San Francisco',
      address: '123 Tech Avenue, Suite 100 Silicon Valley, CA 94025',
      phone: '+1 (555) 123-4567',
      email: 'contact@techmind.com'
    },
    // {
    //   city: 'New York',
    //   address: '456 Innovation Ave, Manhattan, NY 10001',
    //   phone: '+1 (555) 234-5678',
    //   email: 'ny@techmindinfosys.com'
    // },
    // {
    //   city: 'London',
    //   address: '789 Digital Lane, Tech District, UK EC1A 1BB',
    //   phone: '+44 20 7123 4567',
    //   email: 'london@techmindinfosys.com'
    // }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const contactCards = [
    {
      title: 'Visit Our Office',
      description: '123 Tech Boulevard, San Francisco, CA 94105',
      button: { label: 'Get Directions', href: 'https://maps.google.com/?q=123+Tech+Boulevard,+San+Francisco,+CA+94105' },
      bg: 'url("https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?auto=format&fit=crop&w=800&q=80")',
    },
    {
      title: 'Support Team',
      description: (
        <>
          support@techmindinfosys.com<br />
          +1 (555) 123-4567
        </>
      ),
      button: { label: 'Email Support', href: 'mailto:support@techmindinfosys.com' },
      bg: 'url("https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?auto=format&fit=crop&w=800&q=80")',
    },
    {
      title: 'Corporate Inquiries',
      description: (
        <>
          partnerships@techmindinfosys.com<br />
          +1 (555) 987-6543
        </>
      ),
      button: { label: 'Email Us', href: 'mailto:partnerships@techmindinfosys.com' },
      bg: 'url("https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=800&q=80")',
    },
  ];

  return (
    <div className="min-h-screen bg-[#10182B]">
      {/* Hero Section */}
      <section className="relative h-[400px] md:h-[500px] pt-20 flex items-center justify-center text-white">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=1200&q=80)' }}
        >
          <div className="absolute inset-0 bg-[#10182B]/80" />
        </div>
        <div className="relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Let&apos;s Connect</h1>
          <p className="text-lg md:text-2xl text-blue-100 max-w-2xl mx-auto">Have questions or ready to start a project? We&apos;re here to help.</p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-[#10182B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contactCards.map((card, idx) => (
              <div
                key={card.title}
                className="relative rounded-2xl overflow-hidden shadow-lg min-h-[320px] flex flex-col justify-end"
                style={{ backgroundImage: card.bg, backgroundSize: 'cover', backgroundPosition: 'center' }}
              >
                <div className="absolute inset-0 bg-[#10182B]/70" />
                <div className="relative z-10 p-8 flex flex-col h-full justify-end">
                  <h3 className="text-2xl font-bold text-white mb-2">{card.title}</h3>
                  <div className="text-white text-base mb-6">{card.description}</div>
                  <a
                    href={card.button.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-5 py-2 rounded-lg bg-white/90 text-[#10182B] font-semibold hover:bg-white transition-colors text-base"
                  >
                    {card.button.label}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map Section */}
      <section className="py-20 bg-[#10182B]">
        <CommonContactUs/>

        {/* Embedded Google Map */}
        <div className="max-w-7xl mx-auto my-20">
        <div className=" rounded-2xl overflow-hidden shadow-lg border border-[#222846]">
          <div className="bg-gray-800/30 p-4 rounded-xl border border-gray-700/30 overflow-hidden shadow-2xl shadow-gray-900/20 aspect-w-16 aspect-h-9">
            <iframe
              title="Silicon Valley Office Map"
              src="https://www.google.com/maps?q=123+Tech+Avenue,+Suite+100,+Silicon+Valley,+CA+94025&output=embed"
              width="100%"
              height="200"
              style={{ border: 0, filter: 'grayscale(1) invert(0.9)' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
          </div>
        </div>

        {/* <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Send Us a Message
              </h2>
              <p className="text-gray-600 mb-8">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-green-50 border border-green-200 rounded-lg p-6 text-center"
                >
                  <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-green-900 mb-2">
                    Message Sent Successfully!
                  </h3>
                  <p className="text-green-700">
                    Thank you for contacting us. We'll get back to you soon.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter your company name"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter your phone number"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="What is this regarding?"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Tell us about your project or inquiry..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full inline-flex items-center justify-center px-8 py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors duration-200"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Our Offices
              </h2>
              <p className="text-gray-600 mb-8">
                Visit us at any of our global locations or reach out to the nearest office.
              </p>

              <div className="space-y-6">
                {offices.map((office, index) => (
                  <div key={index} className="bg-white p-6 rounded-lg shadow-lg border border-gray-100">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      {office.city}
                    </h3>
                    <div className="space-y-2 text-gray-600">
                      <div className="flex items-start space-x-3">
                        <MapPin className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                        <span>{office.address}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Phone className="w-5 h-5 text-blue-600" />
                        <a href={`tel:${office.phone}`} className="hover:text-blue-600 transition-colors duration-200">
                          {office.phone}
                        </a>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Mail className="w-5 h-5 text-blue-600" />
                        <a href={`mailto:${office.email}`} className="hover:text-blue-600 transition-colors duration-200">
                          {office.email}
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 bg-gray-200 rounded-lg h-64 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <MapPin className="w-12 h-12 mx-auto mb-4" />
                  <p>Interactive Map</p>
                  <p className="text-sm">Google Maps integration</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div> */}
      </section>

      

      {/* FAQ Section */}
      {/* <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find answers to common questions about our services
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {[
              {
                question: "How quickly can you help us find talent?",
                answer: "Our typical time-to-fill ranges from 2-4 weeks depending on the role complexity and requirements."
              },
              {
                question: "Do you work with remote positions?",
                answer: "Yes, we specialize in both remote and on-site positions across various industries and locations."
              },
              {
                question: "What industries do you serve?",
                answer: "We serve technology, healthcare, finance, manufacturing, and many other industries."
              },
              {
                question: "How do you ensure quality candidates?",
                answer: "We use advanced screening processes, technical assessments, and thorough background checks."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-gray-50 p-6 rounded-lg"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-600">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section> */}
    </div>
  );
};

export default ContactPage; 